import { FC, useCallback, useState } from 'react'
import { IconTrash } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTrigger,
} from '../../atoms/alert-dialog'
import { Input } from '../../atoms/inputs/Input'
import Typography from '../../atoms/Typography'
import DeleteButton from 'src/components/atoms/buttons/DeleteButton'
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton'
import { api } from '../../../api/root'
import { enqueueSnackbar } from 'notistack'

interface DeleteMyProjectDialogProps {
  projectId: string
  projectName: string
}

const DeleteMyProjectDialog: FC<DeleteMyProjectDialogProps> = ({ projectId, projectName }) => {
  const [opened, setOpened] = useState(false)
  const [confirmName, setConfirmName] = useState('')

  const handleClose = useCallback(() => {
    setOpened(false)
    setConfirmName('')
  }, [])

  const { mutate, isPending } = api.project.delete.useMutation({
    onSuccess: () => {
      enqueueSnackbar(`Project "${projectName}" deleted successfully`, { variant: 'success' })
      handleClose()
    },
  })

  const handleDelete = useCallback(() => {
    mutate(projectId)
  }, [mutate, projectId])

  return (
    <AlertDialog open={opened} onOpenChange={setOpened}>
      <AlertDialogTrigger asChild>
        <DeleteButton className="opacity-100" />
      </AlertDialogTrigger>
      <AlertDialogContent>
        <div className="flex flex-col items-center gap-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFADAD]">
            <IconTrash className="h-6 w-6 text-red-600" />
          </div>
          <div className="flex flex-col justify-center gap-6">
            <Typography variant="2xl-bold" className="text-center text-green-50">
              Delete Project
            </Typography>
            <Typography variant="base" className="text-center text-gray-50">
              You&apos;re about to delete the{' '}
              <Typography variant="base-semibold" className="inline">
                {projectName}
              </Typography>{' '}
              project. Please type the project name to confirm deletion.
            </Typography>
            <Input
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder="Enter project name"
              className="text-center"
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={handleClose} className="w-40">
            Cancel
          </AlertDialogCancel>
          <PrimaryButton
            disabled={isPending || confirmName !== projectName}
            isLoading={isPending}
            onClick={handleDelete}
            className="w-40 bg-red-600 hover:bg-red-500 active:bg-red-500"
          >
            Delete
          </PrimaryButton>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteMyProjectDialog
